import React from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
  Grid,
  Paper,
  Container,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LinkedIn,
  GitHub,
  Download,
  Code,
  Cloud,
} from '@mui/icons-material';

const Hero = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        width: '100vw',
        position: 'relative',
        left: '50%',
        right: '50%',
        marginLeft: '-50vw',
        marginRight: '-50vw',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: `linear-gradient(135deg, rgba(10, 50, 100, 0.6) 0%, rgba(100, 20, 60, 0.7) 100%), url('/images/city_bg.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        overflow: 'hidden',
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: `linear-gradient(45deg, ${theme.palette.primary.light}20, ${theme.palette.secondary.light}20)`,
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -100,
          left: -100,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: `linear-gradient(45deg, ${theme.palette.secondary.light}15, ${theme.palette.primary.light}15)`,
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
              <Typography
                variant="h1"
                sx={{
                  mb: 2,
                  background: `linear-gradient(45deg, #ffffff, ${theme.palette.primary.light})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 700,
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                }}
              >
                Douglas Hineline
              </Typography>
              
              <Typography
                variant="h2"
                sx={{ 
                  mb: 3, 
                  fontWeight: 600,
                  color: 'white',
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                }}
              >
                Product Manager & Tech Lead
              </Typography>

              <Typography
                variant="h5"
                sx={{ 
                  mb: 4, 
                  lineHeight: 1.6, 
                  maxWidth: 600,
                  color: 'rgba(255, 255, 255, 0.9)',
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                }}
              >
                I turn ideas into reality at lightning speed. From concept to launch, I build teams that ship amazing products and iterate like there's no tomorrow.
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Grid container spacing={2} justifyContent={isMobile ? 'center' : 'flex-start'}>
                  <Grid item>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<Code />}
                      href="#work"
                      sx={{ 
                        px: 4, 
                        py: 1.5,
                        background: 'rgba(255, 255, 255, 0.9)',
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 1)',
                        },
                      }}
                    >
                      View My Work
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<Download />}
                      href="#contact"
                      sx={{ 
                        px: 4, 
                        py: 1.5,
                        borderColor: 'rgba(255, 255, 255, 0.7)',
                        color: 'white',
                        '&:hover': {
                          borderColor: 'white',
                          background: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      Get In Touch
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: isMobile ? 'center' : 'flex-start' }}>
                <Button
                  variant="text"
                  startIcon={<LinkedIn />}
                  href="https://www.linkedin.com/in/dhineline/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      color: 'white',
                      background: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  LinkedIn
                </Button>
                <Button
                  variant="text"
                  startIcon={<GitHub />}
                  href="https://github.com/dhineline"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      color: 'white',
                      background: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  GitHub
                </Button>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Paper
                elevation={12}
                sx={{
                  p: 3,
                  mb: 2.5,
                  borderRadius: 4,
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                }}
              >
                <Box
                  sx={{
                    width: 210,
                    height: 210,
                    mx: 'auto',
                    mb: 2,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, #1976d2 0%, rgba(100, 20, 60, 0.7) 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Avatar
                    src="/images/doesitevenlooklikeme.png"
                    sx={{
                      width: 200,
                      height: 200,
                      opacity: 0.5,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                    }}
                  />
                </Box>
                
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                    <Cloud sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Digital Change Agent
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    GenAI • Systems Design • Product
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Hero; 